import belgium from './belgium.json'

const QMUSIC_RED = '#ed3524'
const NEON_RED = '#ff3b2a'
const HANDOFF_ZOOM = 12

export const BELGIUM_BOUNDS = [
  [2.5, 49.5],
  [6.4, 51.5],
]

const WORLD_RING = [
  [-180, -85],
  [180, -85],
  [180, 85],
  [-180, 85],
  [-180, -85],
]

// Dark brown with neon red, matching highlights.qmusic.be
const QMUSIC_PALETTE = {
  background: '#1c0b09',
  land: '#2a100d',
  water: '#120605',
  building: '#351411',
  road: '#4a1b16',
  roadCasing: '#1c0b09',
  boundary: QMUSIC_RED,
  label: '#ffffff',
  labelHalo: '#1c0b09',
  outsideBelgium: { color: '#000000', opacity: 0.6 },
  glow: QMUSIC_RED,
  neonRoad: NEON_RED,
}

const belgiumRings = belgium.geometry.coordinates.map((polygon) => polygon[0])

const belgiumHighlightLayers = ({ outsideBelgium, glow }) => {
  const mask = {
    id: 'belgium-mask',
    type: 'fill',
    source: 'belgium-mask',
    paint: {
      'fill-color': outsideBelgium.color,
      'fill-opacity': ['interpolate', ['linear'], ['zoom'], HANDOFF_ZOOM - 1, outsideBelgium.opacity, HANDOFF_ZOOM, 0],
    },
  }

  if (!glow) return [mask]

  const lineLayer = (id, paint) => ({
    id,
    type: 'line',
    source: 'belgium-border',
    maxzoom: HANDOFF_ZOOM,
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint,
  })

  return [
    mask,
    lineLayer('belgium-glow-wide', { 'line-color': glow, 'line-width': 22, 'line-blur': 18, 'line-opacity': 0.4 }),
    lineLayer('belgium-glow', { 'line-color': glow, 'line-width': 8, 'line-blur': 5, 'line-opacity': 0.9 }),
    lineLayer('belgium-border', { 'line-color': '#ffd6d0', 'line-width': 2 }),
  ]
}

const recolor = (layer, palette) => {
  const { id, type } = layer
  const withPaint = (paint) => ({ ...layer, paint: { ...layer.paint, ...paint } })

  switch (type) {
    case 'background':
      return withPaint({ 'background-color': palette.background })
    case 'fill':
      return withPaint({
        'fill-color': id.includes('water') ? palette.water : id === 'building' ? palette.building : palette.land,
      })
    case 'line':
      return withPaint({
        'line-color': id.includes('waterway')
          ? palette.water
          : id.includes('boundary')
          ? palette.boundary
          : id.includes('casing')
          ? palette.roadCasing
          : palette.neonRoad && /motorway_inner|major_inner/.test(id)
          ? palette.neonRoad
          : palette.road,
      })
    case 'symbol':
      return withPaint({ 'text-color': palette.label, 'text-halo-color': palette.labelHalo })
    default:
      return layer
  }
}

// Past this zoom the generalized outline drifts from the tiles, so the basemap's own border takes over
const handOffBorder = (layer) => (layer.id === 'boundary_2' ? { ...layer, minzoom: HANDOFF_ZOOM, maxzoom: 24 } : layer)

// Takes a style JSON and returns it recolored, with Belgium highlighted; applied before the map renders anything
export const applyMapTheme = (style) => {
  const palette = QMUSIC_PALETTE
  const layers = style.layers.map((layer) => handOffBorder(recolor(layer, palette)))
  const firstSymbolIndex = layers.findIndex(({ type }) => type === 'symbol')
  const insertAt = firstSymbolIndex === -1 ? layers.length : firstSymbolIndex
  layers.splice(insertAt, 0, ...belgiumHighlightLayers(palette))

  return {
    ...style,
    sources: {
      ...style.sources,
      'belgium-mask': {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'Polygon', coordinates: [WORLD_RING, ...belgiumRings] } },
      },
      'belgium-border': { type: 'geojson', data: belgium },
    },
    layers,
  }
}
